// AboutAlvanium.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import "./AboutAlvanium.css";

// Import all icons with unique names
import jiraIcon from "./icons/jira.png";
import jiraServiceIcon from "./icons/Jira-Service-Management.png";
import confluenceIcon from "./icons/Confluence.png";
import mondayIcon from "./icons/monday.png";
import zendeskIcon from "./icons/zendesk.png";
import appIcon from "./icons/app.png";
import phoneIcon from "./icons/phone.png";
import empIcon from "./icons/emp.png";
import aboutIcon from "./icons/about.png";
import Header from './Header';
import Footer from './Footer';

function AboutAlvanium() {
  const navigate = useNavigate();

  // Navigation handlers
  const handleNavigateToContact = () => {
    navigate('/contact');
  };

  const handleNavigateToProducts = () => {
    navigate('/products');
  };

  // Services data for rotation
  const servicesData = [
    {
      id: 1,
      icon: jiraIcon,
      title: "Jira",
      description: "Streamline project management with customized Jira solutions.",
      color: "#0052CC",
      bgGradient: "linear-gradient(135deg, #0052CC, #2684FF)"
    },
    {
      id: 2,
      icon: confluenceIcon,
      title: "Confluence",
      description: "Create better collaboration and organized knowledge management.",
      color: "#172B4D",
      bgGradient: "linear-gradient(135deg, #172B4D, #42526E)"
    },
    {
      id: 3,
      icon: jiraServiceIcon,
      title: "Jira Service Management",
      description: "Deliver efficient IT service and customer support experiences.",
      color: "#0052CC",
      bgGradient: "linear-gradient(135deg, #0052CC, #4C9AFF)"
    },
    {
      id: 4,
      icon: zendeskIcon,
      title: "Zendesk",
      description: "Enhance customer service with powerful support platforms.",
      color: "#03363D",
      bgGradient: "linear-gradient(135deg, #03363D, #0F7B8C)"
    },
    {
      id: 5,
      icon: mondayIcon,
      title: "monday.com",
      description: "Improve team collaboration and workflow visibility.",
      color: "#FF4D4D",
      bgGradient: "linear-gradient(135deg, #FF4D4D, #FF6B6B)"
    },
    {
      id: 6,
      icon: appIcon,
      title: "Workflow Customization",
      description: "Tailor workflows to match your business processes.",
      color: "#6C5CE7",
      bgGradient: "linear-gradient(135deg, #6C5CE7, #A29BFE)"
    },
    {
      id: 7,
      icon: phoneIcon,
      title: "Android App Development",
      description: "Build scalable, user-friendly Android applications.",
      color: "#00B894",
      bgGradient: "linear-gradient(135deg, #00B894, #00D2D3)"
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const servicesRef = useRef(null);

  // Auto-rotate every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % servicesData.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [servicesData.length]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (servicesRef.current) {
      observer.observe(servicesRef.current);
    }
  }, []);

  // Get 3 services to display
  const getVisibleServices = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % servicesData.length;
      visible.push({
        ...servicesData[index],
        position: i
      });
    }
    return visible;
  };

  const visibleServices = getVisibleServices();

  return (
    <div className="alv-App">
      <Header/>
      {/* ================= HERO SECTION ================= */}
      <section className="alv-hero">
        <div className="alv-hero-left">
          <h1>
            Custom Software &amp;
            <br />
            <span className="alv-gradient-text">Digital Solutions</span>
          </h1>

          <h3>
            We build custom software, business applications, and scalable digital
            solutions that improve productivity and streamline workflows.
          </h3>

          <p>
            Alvanium develops custom software, business apps, and digital solutions
            that help organizations simplify operations, improve efficiency, and
            achieve sustainable business growth.
          </p>

          <div className="alv-buttons">
            <button className="alv-primary" onClick={handleNavigateToContact}>Get Started</button>
            <button className="alv-secondary" onClick={handleNavigateToProducts}>Explore Solutions</button>
          </div>
        </div>

        <div className="alv-hero-right">
          <div className="alv-image-card">
            <div className="alv-image-ring"></div>
            <img src={empIcon} alt="Custom Software Solutions" />
          </div>
        </div>
      </section>

      {/* ================= WHO WE ARE SECTION ================= */}
      <section className="alv-about-section">
        <div className="alv-about-image">
          <div className="alv-image-box">
            <div className="alv-image-ring-about"></div>
            <img src={aboutIcon} alt="Who We Are" />
          </div>
        </div>

        <div className="alv-about-content">
          {/* <span className="alv-section-tag">ABOUT ALVANIUM</span> */}

          <h2>
            Who <span className="alv-gradient-text">We Are</span>
          </h2>

          <p>
            Alvanium is a technology company that builds custom software and
            digital solutions for businesses.
          </p>

          <p>
            We develop scalable applications, business platform apps, and workflow
            solutions that improve productivity and simplify operations.
          </p>

          <p>
            Our goal is to help organizations work smarter, collaborate better,
            and achieve long-term business growth.
          </p>

          <div className="alv-about-stats">
            <div className="alv-stat-card">
              <h3>100%</h3>
              <span>Client Focused</span>
            </div>

            <div className="alv-stat-card">
              <h3>Scalable</h3>
              <span>Technology</span>
            </div>

            <div className="alv-stat-card">
              <h3>24/7</h3>
              <span>Innovation</span>
            </div>
          </div>
        </div>
      </section>

      {/* ================= OUR JOURNEY ================= */}
      <section className="alv-journey-section">
        <div className="alv-journey-content">
          {/* <span className="alv-section-tag">OUR STORY</span> */}

          <h2>
            Our <span className="alv-gradient-text">Journey</span>
          </h2>

          <p>
            Alvanium was created with the vision of helping organizations work
            more efficiently through smart technology solutions.
          </p>

          <p>
            As businesses continue to evolve, managing workflows,
            collaboration, and productivity becomes increasingly important.
          </p>

          <p>
            Our journey started with a commitment to providing practical and
            reliable digital solutions that solve real business challenges.
          </p>

          <p>
            Today, Alvanium continues to expand its services and technologies
            to support organizations with modern and scalable solutions.
          </p>
        </div>

        <div className="alv-journey-timeline">
          <div className="alv-timeline-card">
            <div className="alv-timeline-circle"></div>
            <h3>Vision</h3>
            <p>Helping organizations work smarter through technology.</p>
          </div>

          <div className="alv-timeline-card">
            <div className="alv-timeline-circle"></div>
            <h3>Innovation</h3>
            <p>Creating practical digital solutions for business challenges.</p>
          </div>

          <div className="alv-timeline-card">
            <div className="alv-timeline-circle"></div>
            <h3>Growth</h3>
            <p>Delivering scalable platforms that support long-term success.</p>
          </div>
        </div>
      </section>

      {/* ================= MISSION & VISION ================= */}
      <section className="alv-mission-section">
        <div className="alv-mission-header">
          {/* <span className="alv-section-badge">OUR PURPOSE</span> */}
          <h2>
            Mission <span className="alv-gradient-text">&amp; Vision</span>
          </h2>
          <p className="alv-mission-subtitle">
            Our commitment is to deliver innovative technology solutions
            that empower businesses and create lasting value.
          </p>
        </div>

        <div className="alv-mission-circles-container">
          <div className="alv-mission-circle">
            <div className="alv-circle-content">
              <h3>Our Mission</h3>
              <p>
                To empower businesses with reliable, scalable, and innovative
                digital solutions that improve productivity and simplify
                workflows.
              </p>
            </div>
          </div>

          <div className="alv-mission-circle">
            <div className="alv-circle-content">
              <h3>Our Vision</h3>
              <p>
                To become a trusted technology partner by delivering impactful
                solutions that help organizations grow and succeed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CORE VALUES - FLOW STYLE ================= */}
      <section className="alv-values-flow-section">
        <div className="alv-values-flow-header">
          {/* <span className="alv-values-flow-badge">OUR FOUNDATION</span> */}
          <h2>
            Core <span className="alv-gradient-text">Values</span>
          </h2>
          <p>
            These values guide every solution we build and every partnership
            we create, helping us deliver meaningful technology experiences.
          </p>
        </div>

        <div className="alv-values-flow-container">
          <div className="alv-flow-item">
            <div className="alv-flow-number">01</div>
            <div className="alv-flow-content">
              <h3>Innovation</h3>
              <p>Continuously exploring better solutions and technologies to stay ahead</p>
            </div>
            <div className="alv-flow-line"></div>
          </div>

          <div className="alv-flow-item">
            <div className="alv-flow-number">02</div>
            <div className="alv-flow-content">
              <h3>Customer Success</h3>
              <p>Prioritizing customer goals and delivering long-term value</p>
            </div>
            <div className="alv-flow-line"></div>
          </div>

          <div className="alv-flow-item">
            <div className="alv-flow-number">03</div>
            <div className="alv-flow-content">
              <h3>Quality</h3>
              <p>Maintaining high standards in every solution we deliver</p>
            </div>
            <div className="alv-flow-line"></div>
          </div>

          <div className="alv-flow-item">
            <div className="alv-flow-number">04</div>
            <div className="alv-flow-content">
              <h3>Collaboration</h3>
              <p>Building strong relationships and fostering teamwork</p>
            </div>
            <div className="alv-flow-line"></div>
          </div>

          <div className="alv-flow-item">
            <div className="alv-flow-number">05</div>
            <div className="alv-flow-content">
              <h3>Continuous Improvement</h3>
              <p>Learning, adapting, and growing consistently</p>
            </div>
            <div className="alv-flow-line"></div>
          </div>
        </div>
      </section>

      {/* ================= WHAT WE DO - ROTATING CARDS ================= */}
      <section className="alv-services-section" ref={servicesRef}>
        <div className="alv-services-header">
          {/* <span className="alv-values-flow-badge">OUR SERVICES</span> */}
          <h2>
            Our <span className="alv-gradient-text">Services</span>
          </h2>
          <p>
            We provide technology solutions designed to improve workflows,
            collaboration, and business efficiency.
          </p>
        </div>

        <div className="alv-services-grid alv-rotating">
          {visibleServices.map((service, index) => (
            <div
              key={service.id}
              className={`alv-service-card alv-rotating-card ${isVisible ? 'alv-visible' : ''}`}
              style={{
                animationDelay: `${index * 0.2}s`,
                '--card-color': service.color,
                '--card-gradient': service.bgGradient
              }}
            >
              <div className="alv-card-glow" style={{ background: service.bgGradient }}></div>
              <div className="alv-service-icon-img" style={{ borderColor: service.color }}>
                <img src={service.icon} alt={service.title} />
              </div>
              <h3 style={{ color: service.color }}>{service.title}</h3>
              <p>{service.description}</p>
              <div className="alv-card-shine"></div>
              <div className="alv-card-border" style={{ background: service.bgGradient }}></div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= WHY CHOOSE ALVANIUM ================= */}
      <section className="alv-why-new-section">
        <div className="alv-why-new-header">
          {/* <span className="alv-why-new-badge">WHY ALVANIUM</span> */}
          <h2>
            Why Choose <span className="alv-gradient-text">Alvanium</span>
          </h2>
          <p>
            We combine technology, innovation, and customer-focused solutions
            to help businesses improve efficiency, collaboration, and long-term growth.
          </p>
        </div>

        <div className="alv-why-new-container">
          <div className="alv-why-new-item" style={{ animationDelay: '0.1s' }}>
            <h3>Customized Solutions</h3>
            <p>Tailored specifically for your business</p>
            <div className="alv-why-new-hover-line"></div>
          </div>

          <div className="alv-why-new-item" style={{ animationDelay: '0.3s' }}>
            <h3>Scalable Technology</h3>
            <p>Flexible systems for future growth</p>
            <div className="alv-why-new-hover-line"></div>
          </div>

          <div className="alv-why-new-item" style={{ animationDelay: '0.5s' }}>
            <h3>Reliable Support</h3>
            <p>Continuous support and improvements</p>
            <div className="alv-why-new-hover-line"></div>
          </div>

          <div className="alv-why-new-item" style={{ animationDelay: '0.7s' }}>
            <h3>Modern Approach</h3>
            <p>Using current technologies and best practices</p>
            <div className="alv-why-new-hover-line"></div>
          </div>

          <div className="alv-why-new-item" style={{ animationDelay: '0.9s' }}>
            <h3>User-Focused</h3>
            <p>Creating intuitive and easy-to-use solutions</p>
            <div className="alv-why-new-hover-line"></div>
          </div>
        </div>
      </section>

      {/* ================= TEAM COLLABORATION ================= */}
      <section className="alv-team-circle-section">
        <div className="alv-team-circle-header">
          {/* <span className="alv-team-circle-badge">COLLABORATION</span> */}
          <h2>
            Working Together <br />
            Towards <span className="alv-gradient-text">Innovation</span>
          </h2>
          <p>
            At Alvanium, we believe successful solutions are created through
            collaboration, creativity, and continuous improvement.
          </p>
        </div>

        <div className="alv-team-circle-grid">
          <div className="alv-team-circle-item">
            <div className="alv-team-circle-shape">
              <div className="alv-team-circle-content">
                <h3>Collaboration</h3>
                <p>Working together with clients to achieve shared success</p>
              </div>
            </div>
          </div>

          <div className="alv-team-circle-item">
            <div className="alv-team-circle-shape">
              <div className="alv-team-circle-content">
                <h3>Innovation</h3>
                <p>Transforming ideas into practical digital solutions</p>
              </div>
            </div>
          </div>

          <div className="alv-team-circle-item">
            <div className="alv-team-circle-shape">
              <div className="alv-team-circle-content">
                <h3>Growth</h3>
                <p>Delivering scalable technology for long-term business value</p>
              </div>
            </div>
          </div>

          <div className="alv-team-circle-item">
            <div className="alv-team-circle-shape">
              <div className="alv-team-circle-content">
                <h3>Impact</h3>
                <p>Creating measurable results through smarter workflows</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CALL TO ACTION ================= */}
      <section className="alv-cta-section">
        <div className="alv-cta-content">
          {/* <span className="alv-section-badge">GET STARTED TODAY</span> */}

          <h2>
            Let's Build <span>Smarter Solutions</span> Together
          </h2>

          <p>
            Partner with Alvanium to simplify workflows, improve productivity,
            and create innovative digital experiences.
          </p>

          <div className="alv-cta-buttons">
            <button className="alv-cta-btn alv-primary-btn" onClick={handleNavigateToContact}>Contact Us</button>
            {/* <button className="alv-cta-btn alv-secondary-btn" onClick={handleNavigateToContact}>Get Started</button> */}
          </div>
        </div>
      </section>
      <Footer/>
    </div>
  );
}

export default AboutAlvanium;